import React from "react";
import { useAppToast } from "../../hooks/useAppToast";
import StarterContainer from "../../components/StarterContainer";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Link } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormError } from "../../components/FormError";
import { registerStudent } from "../../services/shared/auth/register.service";
import { useAppNavigate } from "../../lib/AppNavigatorHook";
import { toast } from "react-hot-toast";
import { useLoading } from "../../lib/LoadingHook";
import { ErrorBox } from "../../components/ui/error-box";
import { isBadRequest } from "../../lib/errorUtils";
import { AxiosError } from "axios";

// Define a schema using Yup for form validation
const schema = yup.object().shape({
  name: yup.string().required("Nome é obrigatório"),
  email: yup
    .string()
    .email("Formato de email inválido")
    .required("Email é obrigatório"),
  password: yup
    .string()
    .min(8, "Senha deve ter no mínimo 8 caracteres")
    .required("Senha é obrigatória"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), undefined], "As senhas devem coincidir")
    .required("Confirmação de senha é obrigatória"),
});

interface FormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export function RegisterStudentPage() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const appNavigate = useAppNavigate();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const { isLoading, whenLoading } = useLoading();
  const toast = useAppToast();

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const success = await whenLoading(async () => {
      try {
        await registerStudent(
          data.name,
          data.email,
          data.password,
          data.confirmPassword
        );

        return true;
      } catch (error) {
        if (isBadRequest(error)) {
          setErrorMessage(
            (error as AxiosError<{ message: string }>)?.response?.data
              ?.message ?? "Erro ao registrar usuário"
          );
        } else {
          setErrorMessage(
            error instanceof Error ? error.message : "Erro ao registrar usuário"
          );
        }
      }

      return false;
    });

    if (success) {
      toast.success( 
        "Usuário registrado com sucesso! Faça login para continuar."
      );

      appNavigate.goToLogin();
    }
  };

  return (
    <StarterContainer>
      <h2 className="text-2xl font-bold">Criar Conta</h2>
      <p className="mb-4">Faça sua primeira aula gratuita!</p>
      <ErrorBox message={errorMessage} />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="name" className="block font-medium">
            Nome<span className="text-red-500">*</span>
          </label>
          <Input
            id="name"
            placeholder="Insira seu nome"
            type="text"
            {...register("name")}
          />
          <FormError message={errors.name?.message} />
        </div>
        <div className="space-y-2">
          <label htmlFor="email" className="block font-medium">
            Email<span className="text-red-500">*</span>
          </label>
          <Input
            id="email"
            placeholder="Insira seu email"
            type="email"
            {...register("email")}
          />
          <FormError message={errors.email?.message} />
        </div>
        <div className="space-y-2">
          <label htmlFor="password" className="block font-medium">
            Senha<span className="text-red-500">*</span>
          </label>
          <Input
            id="password"
            placeholder="Crie uma senha"
            type="password"
            {...register("password")}
          />
          <FormError message={errors.password?.message} />
          <p className="text-sm text-gray-500">*Mínimo de 8 caracteres</p>
        </div>
        <div className="space-y-2">
          <label htmlFor="confirmPassword" className="block font-medium">
            Confirme a Senha<span className="text-red-500">*</span>
          </label>
          <Input
            id="confirmPassword"
            placeholder="Confirme sua senha"
            type="password"
            {...register("confirmPassword")}
          />
          <FormError message={errors.confirmPassword?.message} />
        </div>
        <Button
          type="submit"
          className="w-full bg-black text-white py-2 rounded-md"
          isLoading={isLoading}
        >
          Criar Conta
        </Button>
      </form>

      <div className="text-center mt-4">
        <p className="text-sm">
          Já possui uma conta?{" "}
          <Link to="/login" className="text-primary font-bold hover:underline">
            Log in
          </Link>
        </p>
      </div>
    </StarterContainer>
  );
}
